import { useState } from 'react';
import PageShell, { PageTitle } from '../components/PageShell';
import CallButton from '../components/CallButton';
import HelpBox from '../components/HelpBox';
import FaqAccordion from '../components/FaqAccordion';
import { ADDRESS, COMPANY_NAME, SUPPORT_PHONE_LABEL, SUPPORT_PHONE_TEL } from '../constants/site';
import { submitLead } from '../utils/submitLead';
import { openZohoChat } from '../utils/openZohoChat';

const CONTACT_FAQS = [
  {
    q: 'Is this the publisher’s own support line?',
    a: 'No. We are an independent technical support service, separate from Software MacKiev and Ancestry. If you prefer, the publisher’s official help is available on their website.',
  },
  {
    q: 'When will someone get back to me?',
    a: 'Calls and chats are answered as soon as a technician is free. If you use the form, expect a reply by phone or email, often the same day.',
  },
  {
    q: 'What do you do with the details I send?',
    a: 'We use them only to deal with your support request, and we never sell them. The Privacy Policy covers this in detail.',
  },
  {
    q: 'Will I be charged just for asking a question?',
    a: 'No. Reading our guides and asking what the problem might be costs nothing. If personal support is needed and a fee applies, you hear the price and decide before anything starts.',
  },
  {
    q: 'Can I send a screenshot of the error?',
    a: 'Yes. The live chat is the easiest place to share one. Otherwise, describe the message word for word in the form and we will take it from there.',
  },
];

export default function ContactPage() {
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const form = event.currentTarget;
    const data = new FormData(form);
    const name = String(data.get('name') || '').trim();
    const email = String(data.get('email') || '').trim();
    const phone = String(data.get('phone') || '').trim();
    const message = String(data.get('message') || '').trim();

    if (!phone) {
      setError('Please add a phone number so we can reach you.');
      return;
    }

    setPending(true);
    try {
      await submitLead({
        name,
        email,
        phone,
        message,
        source: 'contact-page',
        _honey: String(data.get('_honey') || ''),
      });
      setSent(true);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <PageShell>
      <PageTitle
        title="Contact Us"
        lead="Pick whatever is easiest for you: a phone call, a chat message, or the form below. Mentioning your Family Tree Maker edition and computer type helps us help you sooner."
        crumbs={[{ label: 'Contact Us' }]}
      />

      <CallButton />

      <div className="contact-grid">
        <aside className="contact-aside">
          <h2>Reach a real person</h2>
          <p>
            Our technicians handle Family Tree Maker installs, sync trouble, TreeVault, lost files,
            and upgrades for Windows and Mac users every day.
          </p>
          <ul className="contact-channels">
            <li>
              <strong>Phone</strong>
              <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_LABEL}</a>
            </li>
            <li>
              <strong>Live chat</strong>
              <button type="button" className="text-link" onClick={openZohoChat}>
                Open 24×7 live chat
              </button>
            </li>
            <li>
              <strong>Postal address</strong>
              <span>
                {COMPANY_NAME}
                <br />
                {ADDRESS}
              </span>
            </li>
          </ul>
        </aside>

        {sent ? (
          <div className="contact-form contact-form--done">
            <h2>Message received</h2>
            <p>
              Thank you. A technician will contact you soon using the details you gave. If you would
              like an answer sooner, feel free to{' '}
              <button type="button" className="text-link" onClick={openZohoChat}>
                start a live chat
              </button>{' '}
              now.
            </p>
            <button type="button" className="btn-outline" onClick={() => setSent(false)}>
              Send a new message
            </button>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <h2>Ask for a callback</h2>
            <input
              type="text"
              name="_honey"
              className="contact-form__honey"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <label htmlFor="contact-name">
              Your name
              <input id="contact-name" name="name" type="text" autoComplete="name" required />
            </label>
            <div className="contact-form__row">
              <label htmlFor="contact-email">
                Email address
                <input id="contact-email" name="email" type="email" autoComplete="email" required />
              </label>
              <label htmlFor="contact-phone">
                Phone number
                <input id="contact-phone" name="phone" type="tel" autoComplete="tel" required />
              </label>
            </div>
            <label htmlFor="contact-message">
              What do you need help with?
              <textarea
                id="contact-message"
                name="message"
                rows={5}
                required
                placeholder="For example: FTM 2019 on Windows 11, sync stops at 60% with an error…"
              />
            </label>
            {error ? (
              <p className="contact-form__error" role="alert">
                {error}
              </p>
            ) : null}
            <button type="submit" className="submit-btn" disabled={pending}>
              {pending ? 'Sending…' : 'Request my callback'}
            </button>
          </form>
        )}
      </div>

      <CallButton />

      <section className="entry-section">
        <h2>Useful Details to Have Ready</h2>
        <p>You do not need all of these, but each one shortens the time it takes to find the cause:</p>
        <ul>
          <li>Which edition you own. Open the Help menu and choose About to see it.</li>
          <li>Whether you are on Windows 10, Windows 11, or a Mac, and roughly how old the computer is.</li>
          <li>The error message exactly as it appears, or a photo of the screen.</li>
          <li>What changed just before the problem began: an update, a sync, a new computer, a moved folder.</li>
          <li>When you last made a backup, and where it is stored.</li>
          <li>Roughly how many people and photos are in the tree.</li>
        </ul>
      </section>

      <section className="entry-section">
        <h2>Choose How to Contact Us</h2>
        <div className="service-grid">
          <div className="service">
            <h3>Phone Support</h3>
            <p>
              Call <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_LABEL}</a> when you want to talk it
              through. Best for urgent problems and anything that needs step-by-step guidance.
            </p>
          </div>
          <div className="service">
            <h3>24×7 Live Chat</h3>
            <p>Click the chat button at the bottom of any page. Perfect for quick questions or pasting in an error message.</p>
          </div>
          <div className="service">
            <h3>Callback Form</h3>
            <p>
              Leave your details above and we will get back to you by phone or email. Useful if you are
              not at your computer right now.
            </p>
          </div>
          <div className="service">
            <h3>Remote Assistance</h3>
            <p>For complicated problems, you can let a technician see your screen. You stay in control and can stop at any time.</p>
          </div>
        </div>
      </section>

      <HelpBox
        title="Would You Rather Talk Now?"
        text="No need to wait for a callback. Phone us or start a chat and a technician will help you straight away."
      />

      <section className="entry-section">
        <h2>After You Send the Form</h2>
        <ol>
          <li>A technician reads your message and works out what kind of problem it is.</li>
          <li>We reply by phone or email, whichever you prefer.</li>
          <li>We explain what is probably wrong and whether any fee applies, before doing anything.</li>
          <li>Once you agree, we fix it with you and make sure your backups are in order.</li>
        </ol>
      </section>

      <section className="entry-section">
        <h2>Questions About Contacting Us</h2>
        <FaqAccordion items={CONTACT_FAQS} />
      </section>

      <CallButton />
    </PageShell>
  );
}
